import { useContext, useEffect, useMemo, useState } from 'react'
import { Autocomplete, Box, Button, CircularProgress, TextField, Typography, useMediaQuery } from '@mui/material';
import { LoginOutlined } from '@mui/icons-material';
import { UserDetailsContext } from '../context/user-details/user-details'
import { useLocation } from 'react-router-dom';
import PartnerService from '../shared/services/partner/partner.service';
import gaiaTheme from './theme';
import { usePdfExportMode } from '../hooks/use-pdf-export-mode';
import { getRecentClientIds, resolvePartnerOrganisationId, useSwitchClient } from '../hooks/use-switch-client';

interface ISwitchOption {
  id: string;
  name: string;
  group: string;
}

// leftOffset: width in px of the left rail chrome. The rail's Drawer paints above this
// fixed banner, so the banner starts at the rail's right edge and centres its content in
// the visible area. 0 on mobile / wherever the rail is hidden.
export const PartnerBanner = ({ leftOffset = 0 }: { leftOffset?: number }) => {
  const location = useLocation();
  const pdfExportMode = usePdfExportMode();
  const isMobileMode = useMediaQuery(gaiaTheme.breakpoints.down('sm'));
  const partnerService = new PartnerService();
  const userDetailsContext = useContext(UserDetailsContext);
  const { switchToClient, exitToPartner, isSwitching } = useSwitchClient();

  const [showBanner, setShowBanner] = useState<boolean>(false);
  const [organisationName, setOrganisationName] = useState<string | undefined>()
  const [clientBook, setClientBook] = useState<{ id?: string, name: string }[]>([])
  const [switchInputValue, setSwitchInputValue] = useState<string>('')
  const isPrivateReportRoute = location.pathname.startsWith('/private/');

  useEffect(() => {
    const isPartnerInClient = !!(userDetailsContext.userDetails?.isPartner && userDetailsContext.userDetails?.isLoggedInAsClient);
    setShowBanner(isPartnerInClient);
    setOrganisationName(userDetailsContext.userDetails?.organisation?.name);
  }, [location.pathname, userDetailsContext.userDetails]);

  // The switcher lists the partner's whole client book (the same endpoint the Clients
  // page uses), fetched once the banner is visible. The ownership check on the backend
  // reads the partner organisation preserved on the session, so this works while
  // logged into a client.
  useEffect(() => {
    if (!showBanner) return
    partnerService.getAllClients()
      // Belt and braces: whatever shape comes back, the client book is always an array,
      // because this component rendering is what stands between a partner and the app.
      .then(response => setClientBook(Array.isArray(response) ? response : []))
      .catch(err => {
        console.log(err)
        setClientBook([])
      })
  }, [showBanner])

  // Current client excluded; up to five recently opened clients grouped first, the
  // rest alphabetical. Recomputed when user details change so a switch refreshes
  // the recents grouping.
  const switchOptions: ISwitchOption[] = useMemo(() => {
    const currentOrganisationId = userDetailsContext.userDetails?.organisation?.id
    const partnerOrganisationId = resolvePartnerOrganisationId(userDetailsContext.userDetails)
    const recentRank = new Map(getRecentClientIds(partnerOrganisationId).map((id, index) => [id, index]))
    // A client created but never set up has organisation.name null (the app treats that
    // as a real state everywhere else), so the name must never be dereferenced bare:
    // sorting on a null name crashed this memo, and with it the whole app, in production.
    const displayName = (client: { name?: string | null }) => client.name || 'Unnamed client'
    const selectable = clientBook.filter(client => client.id && client.id !== currentOrganisationId)
    const recent = selectable
      .filter(client => recentRank.has(client.id!))
      .sort((a, b) => recentRank.get(a.id!)! - recentRank.get(b.id!)!)
    const rest = selectable
      .filter(client => !recentRank.has(client.id!))
      .sort((a, b) => displayName(a).localeCompare(displayName(b)))
    return [
      ...recent.map(client => ({ id: client.id!, name: displayName(client), group: 'Recent' })),
      ...rest.map(client => ({ id: client.id!, name: displayName(client), group: 'All clients' })),
    ]
  }, [clientBook, userDetailsContext.userDetails])

  if (pdfExportMode) return null;
  if (!showBanner) return null;

  return (
    <Box sx={{
      height: isMobileMode ? '105px' : '48px',
      width: `calc(100% - ${leftOffset}px)`,
      backgroundColor: 'secondary.main',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: 2,
      mt: isPrivateReportRoute ? 0 : isMobileMode ? 13 : 8,
      px: 2,
      borderBottom: '1px solid',
      borderColor: 'divider',
      position: 'fixed',
      top: 0,
      left: `${leftOffset}px`,
      zIndex: (theme) => theme.zIndex.appBar + 1,
      flexDirection: isMobileMode ? 'column' : 'row'
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, maxWidth: isMobileMode ? '80vw' : '60vw', overflow: 'hidden' }} aria-live='polite'>
        {isMobileMode ? (
          <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {organisationName}
          </Typography>
        ) : (
          <>
            <LoginOutlined sx={{ color: 'primary.dark' }} />
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              Currently logged into:
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 700, color: 'primary.dark', textOverflow: 'ellipsis', whiteSpace: 'nowrap', overflow: 'hidden' }}>
              {organisationName}
            </Typography>
          </>
        )}
      </Box>
      {/* The banner's two-row mobile layout has no room for the switcher, so it is
          desktop-only; mobile keeps Exit organisation and the Clients page. */}
      {!isMobileMode && switchOptions.length > 0 &&
        <Autocomplete
          size='small'
          options={switchOptions}
          groupBy={(option) => option.group}
          getOptionLabel={(option) => option.name}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={null}
          inputValue={switchInputValue}
          onInputChange={(_event, value, reason) => setSwitchInputValue(reason === 'reset' ? '' : value)}
          onChange={(_event, option) => { if (option) switchToClient(option.id) }}
          disabled={isSwitching}
          blurOnSelect
          sx={{ width: 280 }}
          renderInput={(params) => (
            <TextField
              {...params}
              placeholder={isSwitching ? 'Switching client account' : 'Switch to another client'}
              inputProps={{ ...params.inputProps, 'aria-label': 'Switch to another client' }}
              sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'common.white' } }}
              InputProps={{
                ...params.InputProps,
                endAdornment: isSwitching
                  ? <CircularProgress size={18} sx={{ mr: 1 }} />
                  : params.InputProps.endAdornment,
              }}
            />
          )}
        />}
      <Button
        sx={{
          ml: isMobileMode ? 0 : 1,
          backgroundColor: 'primary.main',
          color: 'common.white',
          '&:hover': { backgroundColor: 'primary.dark' },
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.dark' }
        }}
        variant='contained'
        disabled={isSwitching}
        onClick={exitToPartner}
      >
        <Typography>
          Exit organisation
        </Typography>
      </Button>
    </Box>
  )
}
